import {
	Button,
	Flex,
	ModalBody,
	ModalFooter,
	ModalHeader,
	Modalv2
} from "@itandi/itandi-bb-ui";
import type { FC } from "react";
import { useContext, useState } from "react";
import { toast } from "react-toastify";
import { useSWRConfig } from "swr";
import { StatusBar } from "~/components/common/parts/status/StatusBar";
import { dealCacheKey } from "~/hooks/useDeals";
import type { CompanyVendor } from "~/models/CompanyVendor";
import type { Deal } from "~/models/Deal";
import { ItandiAccountContext } from "~/models/ItandiAccount";
import { canOperatingDeal } from "~/models/Staff";
import type { Status } from "~/models/Status";
import { isCanceled } from "~/models/Status";
import { isSuccess } from "~/utils/api/api_handler";
import { destroy as destroyCancel } from "~/utils/api/deals/DealCancel";
import Styles from "./DealMainWorkflow.module.scss";

type Props = {
	companyVendor: CompanyVendor | null;
	deal: Deal;
	displayStatuses: Status[];
};

export const DealMainWorkflowClientCancel: FC<Props> = ({
	deal,
	companyVendor,
	displayStatuses
}) => {
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [isModalOpen, setIsModalOpen] = useState(false);
	const account = useContext(ItandiAccountContext);
	const { mutate: globalMutate } = useSWRConfig();

	const handleAcceptDestroy = async (): Promise<void> => {
		setIsSubmitting(true);
		if (deal.dealCancel !== null) {
			const result = await destroyCancel(deal.id, deal.dealCancel.id);
			if (isSuccess(result)) {
				await globalMutate(dealCacheKey(deal.id));
				toast.success("案件の取り下げを撤回しました");
				setIsModalOpen(false);
			} else {
				toast.error("エラーが発生しました");
			}
		}
		setIsSubmitting(false);
	};

	return (
		<div>
			{!isCanceled(deal.statusId) && (
				<div className={Styles.StatusCard}>
					<h2 className={Styles.Header}>
						<span>
							{companyVendor !== null
								? `${companyVendor.name}に依頼中`
								: "施工会社未選択"}
						</span>
					</h2>
					<StatusBar currentId={null} statuses={displayStatuses} />
				</div>
			)}
			<div className={Styles.WorkflowCard}>
				<h3 className={Styles.CancelTitle}>
					{isCanceled(deal.statusId)
						? "この案件は取り下げられました"
						: "この案件は取り下げ申請中です"}
				</h3>
				<div>
					<div className={Styles.CancelReasonLabel}>取り下げ理由</div>
					<div className={Styles.CancelReason}>{deal.dealCancel?.reason}</div>
					{!isCanceled(deal.statusId) && (
						<Flex justifyContent="end">
							<Button
								disabled={!canOperatingDeal(account)}
								onClick={() => {
									setIsModalOpen(true);
								}}
								variant="secondary"
							>
								取り下げを撤回
							</Button>
						</Flex>
					)}
				</div>
				{isModalOpen ? (
					<Modalv2
						isOpen
						onClose={() => {
							setIsModalOpen(false);
						}}
						size="s"
					>
						<ModalHeader title="取り下げを撤回" />
						<ModalBody>案件の取り下げを撤回します。</ModalBody>
						<ModalFooter
							leftArea={
								<Button
									disabled={isSubmitting}
									onClick={() => {
										setIsModalOpen(false);
									}}
									type="button"
									variant="secondary"
								>
									キャンセル
								</Button>
							}
							rightArea={
								<Button
									loading={isSubmitting}
									onClick={handleAcceptDestroy}
									type="button"
									variant="danger"
								>
									取り下げを撤回する
								</Button>
							}
						/>
					</Modalv2>
				) : null}
			</div>
		</div>
	);
};
